import { lazy, Suspense, useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { callHaService } from "../ha";
import logo from "../assets/logo.webp";
import { ALL_DEVICES, ENTITY_IDS, DEFAULT_VISIBILITY, deviceIcon } from "../ui/devices";
import { useHaStates } from "../ui/useHaStates";
import {
  AnimatedTitle, DeviceToggle, Loader, OfflineBanner, RemoteModal, Section, ToastHost, useActionStatus, useToasts,
} from "../ui/components";
import { AmbilightIcon, HeartIcon, MoonIcon, ProjectorIcon, RemoteIcon, SpeakerIcon, Thermometer } from "../ui/Icons";
import { useLivretRoute, hasSeenIntro, markIntroSeen } from "../livret/route";

// Livret d'accueil chargé à la demande : n'alourdit pas l'affichage des commandes
const loadLivret = () => import("../livret/Livret");
const Livret = lazy(loadLivret);

const FIRE_TV = "media_player.fire_tv_192_168_1_53";

const BookIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 6c-2-1.5-5-2-8-1.5V19c3-.5 6 0 8 1.5 2-1.5 5-2 8-1.5V4.5C17 4 14 4.5 12 6Zm0 0v14.5" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M10 8.5v7l5.5-3.5Z" />
  </svg>
);

// Traductions
const translations = {
  fr: {
    loading: "Chargement...",
    notFound: "Séjour non trouvé",
    notStarted: (date) => `Votre séjour commence le ${date}. Les commandes seront disponibles à votre arrivée.`,
    offline: "Connexion à la maison perdue, nouvelle tentative en cours...",
    welcome: (name) => `Bienvenue chez vous, ${name} !`,
    subtitle: "Vous pouvez contrôler tous les appareils de la maison avec votre téléphone.",
    lights: "Lumières",
    cinema: "Cinéma",
    cinemaMode: "Mode cinéma",
    ambilight: "Ambilight",
    volume: "Volume",
    mute: "Couper ou rétablir le son",
    remote: "Télécommande Fire TV",
    bedroom: "Chambre",
    ambiance: "Son ambiance",
    detente: "Détente",
    love: "Love",
    cooldown: "Patientez 30-40 s, les LED se réinitialisent...",
    spa: "Spa",
    remoteUp: "Haut",
    remoteDown: "Bas",
    remoteLeft: "Gauche",
    remoteRight: "Droite",
    remoteBack: "Retour",
    remoteHome: "Accueil",
    remotePlay: "Lecture / Pause",
    sending: "Envoi en cours...",
    openBook: "Livret d'accueil",
    replayIntro: "Revoir la présentation",
    errCommand: "Erreur lors de l'envoi de la commande",
    errCinema: "Erreur lors du changement de mode cinéma",
    errAmbilight: "Erreur lors du changement Ambilight",
    errAmbiance: "Erreur lors du changement d'ambiance",
    errMute: "Erreur lors du changement de mute",
    errApp: (app) => `Erreur lors du lancement de ${app}`,
    blockedAmbiance: "Vous ne pouvez pas activer les ambiances pendant que le vidéoprojecteur est allumé.",
    devices: {
      salon: "Salon",
      chambre: "Chambre",
      bar: "Bar",
      couloir: "Couloir",
      dressing: "Dressing",
      terrasse: "Terrasse",
      fontaine: "Fontaine",
      bulle: "Bulle"
    }
  },
  en: {
    loading: "Loading...",
    notFound: "Stay not found",
    notStarted: (date) => `Your stay starts on ${date}. Controls will be available when you arrive.`,
    offline: "Connection to the house lost, retrying...",
    welcome: (name) => `Welcome home, ${name}!`,
    subtitle: "You can control all the house devices with your phone.",
    lights: "Lights",
    cinema: "Cinema",
    cinemaMode: "Cinema mode",
    ambilight: "Ambilight",
    volume: "Volume",
    mute: "Mute or unmute",
    remote: "Fire TV Remote",
    bedroom: "Bedroom",
    ambiance: "Mood lighting",
    detente: "Relax",
    love: "Love",
    cooldown: "Please wait 30-40 s, LEDs are resetting...",
    spa: "Spa",
    remoteUp: "Up",
    remoteDown: "Down",
    remoteLeft: "Left",
    remoteRight: "Right",
    remoteBack: "Back",
    remoteHome: "Home",
    remotePlay: "Play / Pause",
    sending: "Sending...",
    openBook: "Welcome book",
    replayIntro: "Replay the presentation",
    errCommand: "Could not send the command",
    errCinema: "Could not change cinema mode",
    errAmbilight: "Could not change Ambilight",
    errAmbiance: "Could not change the mood lighting",
    errMute: "Could not mute or unmute",
    errApp: (app) => `Could not launch ${app}`,
    blockedAmbiance: "Mood lighting is unavailable while the projector is on.",
    devices: {
      salon: "Living room",
      chambre: "Bedroom",
      bar: "Bar",
      couloir: "Hallway",
      dressing: "Dressing room",
      terrasse: "Terrace",
      fontaine: "Fountain",
      bulle: "Bubbles"
    }
  },
  es: {
    loading: "Cargando...",
    notFound: "Estancia no encontrada",
    notStarted: (date) => `Su estancia comienza el ${date}. Los controles estarán disponibles a su llegada.`,
    offline: "Conexión con la casa perdida, reintentando...",
    welcome: (name) => `¡Bienvenido a casa, ${name}!`,
    subtitle: "Puedes controlar todos los dispositivos de la casa con tu teléfono.",
    lights: "Luces",
    cinema: "Cine",
    cinemaMode: "Modo cine",
    ambilight: "Ambilight",
    volume: "Volumen",
    mute: "Silenciar o activar el sonido",
    remote: "Control Fire TV",
    bedroom: "Dormitorio",
    ambiance: "Ambiente",
    detente: "Relax",
    love: "Amor",
    cooldown: "Espera 30-40 s, los LEDs se están reiniciando...",
    spa: "Spa",
    remoteUp: "Arriba",
    remoteDown: "Abajo",
    remoteLeft: "Izquierda",
    remoteRight: "Derecha",
    remoteBack: "Atrás",
    remoteHome: "Inicio",
    remotePlay: "Reproducir / Pausa",
    sending: "Enviando...",
    openBook: "Guía de bienvenida",
    replayIntro: "Ver de nuevo la presentación",
    errCommand: "Error al enviar el comando",
    errCinema: "Error al cambiar el modo cine",
    errAmbilight: "Error al cambiar Ambilight",
    errAmbiance: "Error al cambiar el ambiente",
    errMute: "Error al silenciar",
    errApp: (app) => `Error al iniciar ${app}`,
    blockedAmbiance: "No se pueden activar los ambientes mientras el proyector está encendido.",
    devices: {
      salon: "Salón",
      chambre: "Dormitorio",
      bar: "Bar",
      couloir: "Pasillo",
      dressing: "Vestidor",
      terrasse: "Terraza",
      fontaine: "Fuente",
      bulle: "Burbujas"
    }
  }
};

const LANGUAGES = [
  { code: "fr", label: "FR", name: "Français" },
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
];

function readLanguage() {
  try {
    return localStorage.getItem('sejour_language') || 'fr';
  } catch {
    return 'fr';
  }
}

function Sejour() {
  const [secureToken] = useState(() => window.location.pathname.split("/").pop());
  const [sejour, setSejour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notStarted, setNotStarted] = useState(false);
  const [cinemaMode, setCinemaMode] = useState(false);
  const [hyperhdrEnabled, setHyperhdrEnabled] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [detenteEnabled, setDetenteEnabled] = useState(false);
  const [loveEnabled, setLoveEnabled] = useState(false);
  const [cooldownMessage, setCooldownMessage] = useState("");
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const [cinemaCooldown, setCinemaCooldown] = useState(false);
  const [visitorVisibility, setVisitorVisibility] = useState(DEFAULT_VISIBILITY);
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);
  const [language, setLanguage] = useState(readLanguage);

  const t = translations[language] || translations.fr;

  const { toasts, show } = useToasts();
  const { status, track, markError } = useActionStatus();

  // Message non bloquant (remplace alert)
  const notify = (message, kind = "error") => {
    if (kind === "error") markError();
    show(message, kind);
  };

  // Changer de langue et sauvegarder dans localStorage
  const changeLanguage = (lang) => {
    setLanguage(lang);
    try {
      localStorage.setItem('sejour_language', lang);
    } catch {
      // stockage indisponible (navigation privée) : la langue reste valable pour la session
    }
  };

  // Fonction pour obtenir le label traduit d'un dispositif
  const getDeviceLabel = (visKey) => {
    return t.devices[visKey] || visKey;
  };

  // Ref pour cooldown par action (anti-spam intelligent)
  const lastSentAtRef = useRef({});

  // Filtrer selon la visibilité (seulement afficher ce qui est visible pour les visiteurs)
  const lights = ALL_DEVICES.filter((d) => d.type === "light" && (!d.visKey || visitorVisibility[d.visKey]));
  const spa = ALL_DEVICES.filter((d) => d.type === "spa" && (!d.visKey || visitorVisibility[d.visKey]));

  useEffect(() => {
    if (!secureToken) return;

    const fetchSejour = async () => {
      try {
        const snap = await getDoc(doc(db, "sejours", secureToken));
        const data = snap.exists() ? snap.data() : null;
        const now = Date.now();
        if (!data || !data.depart || now > data.depart.toMillis()) {
          setNotFound(true);
        } else {
          setSejour(data);
          if (data.arrivee && now < data.arrivee.toMillis()) setNotStarted(true);
        }
      } catch (err) {
        console.error("Erreur Firestore :", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSejour();
  }, [secureToken]);

  const sessionActive = !!sejour && !notStarted && !notFound;

  const {
    deviceStates, loaded, spaTemp, volume, setVolume, isMuted, setIsMuted, haOnline, setDeviceLocal, refreshSoon,
  } = useHaStates({
    enabled: sessionActive,
    sejourToken: secureToken,
    entityIds: ENTITY_IDS,
    onAccessDenied: () => setNotFound(true),
  });

  // ---- Livret d'accueil ----
  const livretNav = useLivretRoute();
  const { route: livretRoute, openIntro } = livretNav;
  const autoIntroDone = useRef(false);

  // Première ouverture du séjour sur cet appareil : introduction automatique (une seule fois)
  useEffect(() => {
    if (!sessionActive || !visibilityLoaded || autoIntroDone.current) return;
    autoIntroDone.current = true;
    if (!livretRoute && !hasSeenIntro(secureToken)) {
      markIntroSeen(secureToken);
      openIntro();
    }
  }, [sessionActive, visibilityLoaded, livretRoute, secureToken, openIntro]);

  // Sinon, le code du livret est préchargé quand le téléphone est inactif (disponible ensuite hors connexion)
  useEffect(() => {
    if (!sessionActive) return undefined;
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 2500));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = idle(() => { loadLivret().catch(() => {}); });
    return () => cancel(handle);
  }, [sessionActive]);

  const isSectionAvailable = useCallback((sectionId) => ({
    "section-lights": ALL_DEVICES.some((d) => d.type === "light" && visitorVisibility[d.visKey]),
    "section-cinema": !!visitorVisibility.videoprojecteur,
    "section-bedroom": !!visitorVisibility.ambiances,
    "section-spa": ALL_DEVICES.some((d) => d.type === "spa" && visitorVisibility[d.visKey]),
  }[sectionId] || false), [visitorVisibility]);

  const closeLivret = livretNav.close;
  // Depuis la rubrique Équipements : fermer le livret puis faire défiler jusqu'aux commandes
  const goToSection = useCallback((sectionId) => {
    closeLivret();
    setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 350);
  }, [closeLivret]);

  // Charger la configuration de visibilité visiteurs depuis Firestore
  // (en parallèle du séjour ; les sections s'affichent une fois connue pour éviter tout clignotement)
  useEffect(() => {
    const loadVisitorVisibility = async () => {
      try {
        const docRef = doc(db, "settings", "visitorVisibility");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setVisitorVisibility((current) => ({ ...current, ...docSnap.data() }));
        }
      } catch (err) {
        console.error("Erreur chargement visibilité visiteurs:", err);
      } finally {
        setVisibilityLoaded(true);
      }
    };
    loadVisitorVisibility();
  }, []);

  // Synchroniser l'état du vidéoprojecteur avec Firestore en temps réel
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "deviceStates", "videoprojecteur"), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCinemaMode(data.isOn || false);
      }
    }, (err) => {
      console.error("Erreur onSnapshot vidéoprojecteur:", err);
    });
    return () => unsubscribe();
  }, []);

  // Helper : delay
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  // Helper : callService Home Assistant (via le proxy sécurisé)
  const callService = (domain, service, data) => callHaService(domain, service, data, secureToken);

  // Gestion Mode Cinéma (avec cooldown visuel 10s)
  const handleCinemaModeToggle = async () => {
    if (loadingAction || cinemaCooldown) return;

    // Cooldown anti-spam : 2000ms
    const actionKey = 'cinema_mode';
    const now = Date.now();
    if (lastSentAtRef.current[actionKey] && (now - lastSentAtRef.current[actionKey] < 2000)) {
      return;
    }
    lastSentAtRef.current[actionKey] = now;

    navigator.vibrate?.(30);

    setLoadingAction(true);
    setCinemaCooldown(true);
    setTimeout(() => setCinemaCooldown(false), 10000); // 10s de cooldown visuel

    const newState = !cinemaMode;
    setCinemaMode(newState);

    try {
      if (newState) {
        // Allumer le mode cinéma
        setDetenteEnabled(false);
        setLoveEnabled(false);

        // 1. Allumer Fire TV
        await callService("media_player", "turn_on", { entity_id: FIRE_TV });
        await delay(100);

        // 2. Lancer Netflix monkey-first
        try {
          await callService("androidtv", "adb_command", {
            entity_id: FIRE_TV,
            command: "input keyevent 3"
          });
          await delay(180);

          await callService("androidtv", "adb_command", {
            entity_id: FIRE_TV,
            command: "monkey -p com.netflix.ninja 1"
          });
        } catch (netflixErr) {
          console.log("Netflix non lancé:", netflixErr);
        }

        await delay(100);

        // 3. Suite normale: VP + HyperHDR + Ambilight
        await callService("button", "press", { entity_id: "button.videoprojecteur_ir_vp_power_frame_repeats" });
        await delay(100);
        await callService("automation", "trigger", { entity_id: "automation.hyperhdr_on" });
        await delay(100);
        await callService("script", "turn_on", { entity_id: "script.ambilight_on" });
        setHyperhdrEnabled(true);

        // Sauvegarder l'état dans Firestore
        await setDoc(doc(db, "deviceStates", "videoprojecteur"), {
          isOn: true,
          updatedAt: serverTimestamp()
        });
      } else {
        // Éteindre le mode cinéma
        await callService("script", "turn_on", { entity_id: "script.ambilight_off" });
        await delay(100);
        await callService("automation", "trigger", { entity_id: "automation.hyperhdr_off" });
        await delay(100);
        await callService("button", "press", { entity_id: "button.videoprojecteur_ir_vp_off_2x_frame_repeats" });
        setHyperhdrEnabled(false);
        setCooldownMessage(t.cooldown);
        setTimeout(() => setCooldownMessage(""), 40000);

        // Sauvegarder l'état dans Firestore
        await setDoc(doc(db, "deviceStates", "videoprojecteur"), {
          isOn: false,
          updatedAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.error("Erreur mode cinéma:", err);
      setCinemaMode(!newState);
      setHyperhdrEnabled(!newState ? true : false);
      notify(t.errCinema);
    } finally {
      setLoadingAction(false);
    }
  };

  // Gestion Ambilight (toggle seulement quand VP est allumé)
  const handleHyperhdrToggle = async () => {
    if (loadingAction || !cinemaMode) return;
    navigator.vibrate?.(30);

    setLoadingAction(true);
    const newState = !hyperhdrEnabled;
    setHyperhdrEnabled(newState);

    try {
      if (newState) {
        await callService("script", "turn_on", { entity_id: "script.ambilight_on" });
      } else {
        await callService("script", "turn_on", { entity_id: "script.ambilight_off" });
      }
    } catch (err) {
      console.error("Erreur Ambilight:", err);
      setHyperhdrEnabled(!newState);
      notify(t.errAmbilight);
    } finally {
      setLoadingAction(false);
    }
  };

  // Gestion Ambiances Chambre - Switch Détente
  const handleDetenteToggle = async () => {
    if (loadingAction) return;

    if (cinemaMode) {
      navigator.vibrate?.(50);
      notify(t.blockedAmbiance, "info");
      return;
    }

    if (cooldownMessage) {
      navigator.vibrate?.(50);
      notify(cooldownMessage, "info");
      return;
    }

    navigator.vibrate?.(30);
    setLoadingAction(true);
    const newState = !detenteEnabled;
    setDetenteEnabled(newState);

    if (newState && loveEnabled) {
      setLoveEnabled(false);
    }

    try {
      if (newState) {
        await callService("scene", "turn_on", { entity_id: "scene.ambiance_love" });
      } else {
        await callService("scene", "turn_on", { entity_id: "scene.ambiance_off" });
      }
    } catch (err) {
      console.error("Erreur Ambiance Détente:", err);
      setDetenteEnabled(!newState);
      notify(t.errAmbiance);
    } finally {
      setLoadingAction(false);
    }
  };

  // Gestion Ambiances Chambre - Switch Love
  const handleLoveToggle = async () => {
    if (loadingAction) return;

    if (cinemaMode) {
      navigator.vibrate?.(50);
      notify(t.blockedAmbiance, "info");
      return;
    }

    if (cooldownMessage) {
      navigator.vibrate?.(50);
      notify(cooldownMessage, "info");
      return;
    }

    navigator.vibrate?.(30);
    setLoadingAction(true);
    const newState = !loveEnabled;
    setLoveEnabled(newState);

    if (newState && detenteEnabled) {
      setDetenteEnabled(false);
    }

    try {
      if (newState) {
        await callService("scene", "turn_on", { entity_id: "scene.ambiance_love_2" });
      } else {
        await callService("scene", "turn_on", { entity_id: "scene.ambiance_off" });
      }
    } catch (err) {
      console.error("Erreur Ambiance Love:", err);
      setLoveEnabled(!newState);
      notify(t.errAmbiance);
    } finally {
      setLoadingAction(false);
    }
  };

  // Contrôles Audio - Mute
  const handleVolumeMute = async () => {
    if (loadingAction) return;
    navigator.vibrate?.(30);
    setLoadingAction(true);
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    try {
      await callService("media_player", "volume_mute", {
        entity_id: "media_player.n2mini",
        is_volume_muted: newMutedState
      });
    } catch (err) {
      console.error("Erreur Mute:", err);
      setIsMuted(!newMutedState);
      notify(t.errMute);
    } finally {
      setLoadingAction(false);
    }
  };

  // Contrôles Audio - Slider volume
  // Envoi différé : une seule requête quand le curseur s'arrête (au lieu d'une par pixel)
  const volumeTimerRef = useRef(null);
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (loadingAction) return;

    clearTimeout(volumeTimerRef.current);
    volumeTimerRef.current = setTimeout(async () => {
      try {
        await callService("media_player", "volume_set", {
          entity_id: "media_player.n2mini",
          volume_level: newVolume
        });
      } catch (err) {
        console.error("Erreur Volume Set:", err);
      }
    }, 300);
  };

  // Télécommande Fire TV - Envoi de commande ADB avec cooldown par action
  const sendFireTVCommand = async (keycode) => {
    const actionKey = `firetv_${keycode}`;
    const now = Date.now();

    // Cooldown différencié par type de touche
    let cooldown = 150; // DPAD + OK par défaut
    if ([3, 4].includes(keycode)) cooldown = 250; // HOME, BACK
    if ([85].includes(keycode)) cooldown = 250; // PLAY/PAUSE

    if (lastSentAtRef.current[actionKey] && (now - lastSentAtRef.current[actionKey] < cooldown)) {
      return; // Ignorer le spam
    }
    lastSentAtRef.current[actionKey] = now;

    navigator.vibrate?.(30);

    // Désactiver temporairement le bouton (100ms)
    setLoadingAction(true);
    setTimeout(() => setLoadingAction(false), 100);

    try {
      // Envoyer via ADB keyevent
      await callService("androidtv", "adb_command", {
        entity_id: FIRE_TV,
        command: `input keyevent ${keycode}`
      });
    } catch (err) {
      console.error(`Erreur commande Fire TV (keycode ${keycode}):`, err);
      notify(t.errCommand);
    }
  };

  // Lancer une application (monkey-first, plus fiable sur Fire TV)
  // Même séquence qu'avant pour Netflix, Disney+ et Prime Video : HOME, 180 ms, monkey, nouvel essai après 400 ms
  const launchApp = async (actionKey, packageName, appName) => {
    if (loadingAction) return;

    // Cooldown anti-spam : 1200ms
    const now = Date.now();
    if (lastSentAtRef.current[actionKey] && (now - lastSentAtRef.current[actionKey] < 1200)) {
      return;
    }
    lastSentAtRef.current[actionKey] = now;

    navigator.vibrate?.(30);
    setLoadingAction(true);

    try {
      // 1. Envoyer HOME d'abord
      await callService("androidtv", "adb_command", {
        entity_id: FIRE_TV,
        command: "input keyevent 3"
      });

      // 2. Attendre 180ms
      await delay(180);

      // 3. Lancer l'application via monkey (priorité)
      try {
        await callService("androidtv", "adb_command", {
          entity_id: FIRE_TV,
          command: `monkey -p ${packageName} 1`
        });
      } catch (err) {
        // Retry après 400ms
        await delay(400);
        await callService("androidtv", "adb_command", {
          entity_id: FIRE_TV,
          command: `monkey -p ${packageName} 1`
        });
      }
    } catch (err) {
      console.error(`Erreur lancement ${appName}:`, err);
      notify(t.errApp(appName));
    } finally {
      setLoadingAction(false);
    }
  };

  const launchNetflix = () => launchApp('launch_netflix', 'com.netflix.ninja', 'Netflix');
  const launchDisney = () => launchApp('launch_disney', 'com.disney.disneyplus', 'Disney+');
  const launchPrime = () => launchApp('launch_prime', 'com.amazon.avod.thirdpartyclient', 'Prime Video');

  // Lumières et spa : mise à jour immédiate de l'interrupteur, retour arrière si la commande échoue
  const handleToggle = (entity_id) => {
    if (status[entity_id] === "pending") return;
    navigator.vibrate?.(30);

    const previous = !!deviceStates[entity_id];
    setDeviceLocal(entity_id, !previous);

    track(entity_id, async () => {
      try {
        await callService("switch", "toggle", { entity_id });
        refreshSoon();
      } catch (err) {
        setDeviceLocal(entity_id, previous);
        notify(t.errCommand);
        console.error("Erreur Home Assistant :", err);
      }
    });
  };

  if (loading || (sessionActive && !visibilityLoaded)) return <Loader label={t.loading} />;

  if (notFound) {
    return (
      <div className="message-page page">
        <img src={logo} alt="La Maison du Château d'Eau" className="logo" width="240" height="240" />
        <h2 className="page-title">{t.notFound}</h2>
      </div>
    );
  }

  if (notStarted) {
    const locale = { fr: "fr-FR", en: "en-GB", es: "es-ES" }[language];
    const date = sejour.arrivee.toDate().toLocaleString(locale, { dateStyle: "long", timeStyle: "short" });
    return (
      <div className="message-page page">
        <img src={logo} alt="La Maison du Château d'Eau" className="logo" width="240" height="240" />
        <AnimatedTitle text={t.welcome(sejour.nom)} />
        <p className="page-subtitle">{t.notStarted(date)}</p>
      </div>
    );
  }

  let sectionIndex = 0;
  const statesUnknown = !loaded;

  return (
    <div className="page">
      <img src={logo} alt="La Maison du Château d'Eau" className="logo" width="240" height="240" />

      <AnimatedTitle text={t.welcome(sejour?.nom)} />
      <p className="page-subtitle">{t.subtitle}</p>

      {/* Sélecteur de langue */}
      <div className="lang-switch" role="group" aria-label="Langue">
        {LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            lang={lang.code}
            aria-label={lang.name}
            aria-pressed={language === lang.code}
            onClick={() => changeLanguage(lang.code)}
          >
            {lang.label}
          </button>
        ))}
      </div>

      {/* Accès permanents au livret d'accueil */}
      <div className="livret-entry">
        <button type="button" className="btn btn-primary" onClick={() => livretNav.openBook()} onPointerEnter={loadLivret}>
          <BookIcon /> {t.openBook}
        </button>
        <button type="button" className="btn btn-soft" onClick={openIntro} onPointerEnter={loadLivret}>
          <PlayIcon /> {t.replayIntro}
        </button>
      </div>

      {!haOnline && <OfflineBanner text={t.offline} />}

      {lights.length > 0 && (
        <Section id="section-lights" title={t.lights} index={sectionIndex++}>
          <div className="grid">
            {lights.map((d) => (
              <DeviceToggle
                key={d.id}
                label={getDeviceLabel(d.visKey)}
                icon={deviceIcon(d.visKey)}
                checked={deviceStates[d.id]}
                unknown={statesUnknown}
                status={status[d.id]}
                onChange={() => handleToggle(d.id)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Section CINÉMA */}
      {visitorVisibility.videoprojecteur && (
        <Section id="section-cinema" title={t.cinema} index={sectionIndex++}>
          <div className="grid">
            <DeviceToggle
              label={t.cinemaMode}
              icon={<ProjectorIcon />}
              checked={cinemaMode}
              status={status.cinema}
              disabled={loadingAction || cinemaCooldown}
              dimmed={cinemaCooldown}
              onChange={() => track("cinema", handleCinemaModeToggle)}
            />

            {visitorVisibility.ambilight && (
              <DeviceToggle
                label={t.ambilight}
                icon={<AmbilightIcon />}
                checked={hyperhdrEnabled}
                status={status.ambilight}
                disabled={loadingAction || !cinemaMode}
                dimmed={!cinemaMode}
                onChange={() => track("ambilight", handleHyperhdrToggle)}
              />
            )}
          </div>

          {/* Contrôle Volume */}
          <div className="volume">
            <div className="volume-head">
              <label htmlFor="volume">{t.volume}</label>
              <output htmlFor="volume">{Math.round(volume * 100)}%</output>
            </div>
            <div className="volume-row">
              <input
                id="volume"
                className="range"
                style={{ "--val": `${Math.round(volume * 100)}%` }}
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                disabled={loadingAction}
              />
              <button
                type="button"
                className={`btn btn-soft btn-icon ${isMuted ? "is-active" : ""}`}
                onClick={handleVolumeMute}
                disabled={loadingAction}
                aria-label={t.mute}
                aria-pressed={isMuted}
              >
                <SpeakerIcon muted={isMuted} />
              </button>
            </div>
          </div>

          {/* Bouton Télécommande Fire TV */}
          <button
            type="button"
            className="btn btn-primary btn-block mt"
            onClick={() => setShowRemoteModal(true)}
            disabled={loadingAction}
          >
            <RemoteIcon /> {t.remote}
          </button>
        </Section>
      )}

      {/* Section CHAMBRE (Ambiances) */}
      {visitorVisibility.ambiances && (
        <Section id="section-bedroom" title={t.bedroom} index={sectionIndex++}>
          <p className="section-sub">{t.ambiance}</p>
          <div className="grid">
            <DeviceToggle
              label={t.detente}
              icon={<MoonIcon />}
              checked={detenteEnabled}
              status={status.detente}
              disabled={loadingAction || cinemaMode || !!cooldownMessage}
              dimmed={cinemaMode || !!cooldownMessage}
              onChange={() => track("detente", handleDetenteToggle)}
            />
            <DeviceToggle
              label={t.love}
              icon={<HeartIcon />}
              checked={loveEnabled}
              status={status.love}
              disabled={loadingAction || cinemaMode || !!cooldownMessage}
              dimmed={cinemaMode || !!cooldownMessage}
              onChange={() => track("love", handleLoveToggle)}
            />
          </div>

          {cooldownMessage && <p className="note" role="status">{cooldownMessage}</p>}
        </Section>
      )}

      {/* Section SPA */}
      {spa.length > 0 && (
        <Section
          id="section-spa"
          title={t.spa}
          index={sectionIndex++}
          aside={spaTemp !== null && (
            <span className="thermo">
              <Thermometer value={spaTemp} />
              {spaTemp.toFixed(1)}°C
            </span>
          )}
        >
          <div className="grid">
            {spa.map((d) => (
              <DeviceToggle
                key={d.id}
                label={getDeviceLabel(d.visKey)}
                icon={deviceIcon(d.visKey)}
                checked={deviceStates[d.id]}
                unknown={statesUnknown}
                status={status[d.id]}
                onChange={() => handleToggle(d.id)}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Modal Télécommande Fire TV */}
      <RemoteModal
        open={showRemoteModal}
        onClose={() => setShowRemoteModal(false)}
        onKey={sendFireTVCommand}
        onNetflix={launchNetflix}
        onDisney={launchDisney}
        onPrime={launchPrime}
        busy={loadingAction}
        labels={{
          up: t.remoteUp,
          down: t.remoteDown,
          left: t.remoteLeft,
          right: t.remoteRight,
          back: t.remoteBack,
          home: t.remoteHome,
          play: t.remotePlay,
          sending: t.sending,
        }}
      />

      <ToastHost toasts={toasts} />

      {/* Livret d'accueil (introduction, sommaire, rubriques) */}
      {livretRoute && createPortal(
        <Suspense fallback={<div className={`livret-loading ${livretRoute.view === "intro" ? "is-dark" : ""}`}><div className="loader-ring" /></div>}>
          <Livret
            route={livretRoute}
            nav={livretNav}
            language={language}
            onLanguage={changeLanguage}
            guestName={sejour?.nom}
            sejourToken={secureToken}
            spaTemp={spaTemp}
            isSectionAvailable={isSectionAvailable}
            goToSection={goToSection}
            onLeaveIntro={() => markIntroSeen(secureToken)}
          />
        </Suspense>,
        document.body
      )}
    </div>
  );
}

export default Sejour;
